import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import Tooltip from "@/components-legacy/tooltip";
import useDeleteExportMethod from "@babackapp/private-api-client/client/use-delete-export-method";
import useGetExportTasks from "@babackapp/private-api-client/client/use-get-export-tasks";
import { shopQueryKey } from "@babackapp/private-api-client/client/use-shop-suspense";
import {
  ShopExportDto,
  TaskListItemDto,
} from "@babackapp/private-api-client/types";
import { useQueryClient } from "@tanstack/react-query";
import useTranslation, {
  Translation,
} from "@babackapp/translations/use-translation";
import { Button, ButtonLoading } from "@babackapp/ui/components/button";
import { Card, CardContent } from "@babackapp/ui/components/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@babackapp/ui/components/dialog";
import SimpleMenu from "@babackapp/ui/components/simple-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@babackapp/ui/components/select";
import { TrashIcon } from "@heroicons/react/24/solid";
import { format } from "date-fns";
import { DatabaseIcon, MoreHorizontal, Pencil } from "lucide-react";
import { useState } from "react";
import AddExportSettingsDialog from "./add-export-settings-dialog";
import UpdateExportSettingsDialog from "./update-export-settings-dialog";

export default function ExportSettings() {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();

  return (
    <Card>
      <CardContent className="my-8">
        <div className="space-y-6 divide-y divide-gray-200">
          <div className="space-y-6">
            <div className="flex">
              <div className="flex-1">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  {t("settings.integrations.exports.title")}
                </h3>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                  {t("settings.integrations.exports.subtitle")}
                </p>
              </div>
              <div>
                <AddExportSettingsDialog>
                  <Button>Add Export</Button>
                </AddExportSettingsDialog>
              </div>
            </div>
          </div>

          <div className="space-y-6 pt-6">
            {(shop.exports as ShopExportDto[]).map(
              (exportConfig: ShopExportDto) => (
                <FTPExport
                  key={exportConfig.jobId}
                  exportConfig={exportConfig}
                />
              ),
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function FTPExport(props: { exportConfig: ShopExportDto }) {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const queryClient = useQueryClient();
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [tasksIsOpen, setTasksIsOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [sinceDaysFilter, setSinceDaysFilter] = useState<number | undefined>(
    30,
  );

  const { mutateAsync: deleteExport, isPending: isDeletePending } =
    useDeleteExportMethod(shop);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useGetExportTasks(shop.id, props.exportConfig.jobId, {
      status: statusFilter,
      sinceDays: sinceDaysFilter,
    });

  const tasks = data?.pages.flat();
  const lastTask = tasks?.[0];

  const handleDelete = async () => {
    try {
      await deleteExport({ exportId: props.exportConfig.jobId });
      setIsDeleting(false);
      void queryClient.refetchQueries({ queryKey: shopQueryKey(shop.id) });
    } catch {
      // dialog stays open; error toast already shown by useApiMutation
    }
  };

  const isError = lastTask?.status === "error";

  return (
    <>
      <div className="flex justify-between group">
        <div className="flex space-x-4 text-sm">
          <div
            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
              isError
                ? "bg-red-100 text-red-800"
                : "bg-green-100 text-green-800"
            }`}
          >
            <div
              className={`-ml-0.5 mr-1.5 h-1.5 w-1.5 rounded-full ${
                isError ? "bg-red-400" : "bg-green-400"
              }`}
            ></div>
            {props.exportConfig.type.toUpperCase()}
          </div>
          <div>{props.exportConfig.login.host}</div>
          <div className="text-gray-500">
            {`${t("settings.integrations.exports.lastRun")} ${
              props.exportConfig.lastRun > 0
                ? format(props.exportConfig.lastRun, "dd/MM/yyyy HH:mm")
                : t("misc.never")
            }`}
          </div>
          <div className="inline-flex space-x-2 items-center rounded-full px-2.5 py-0.5 text-xs font-medium text-gray-500">
            <Tooltip
              title=""
              description={
                <>
                  {props.exportConfig.filter.status.length > 0 && (
                    <p className="text-black font-semibold">
                      {props.exportConfig.filter.status.length}{" "}
                      {t("settings.integrations.exports.status")}
                    </p>
                  )}
                  {props.exportConfig.filter.status.map(
                    (subscription, index) => {
                      return (
                        <div className="mb-1" key={`status-${index}`}>
                          {t(
                            ("returnDetail.status." +
                              subscription) as Translation,
                          )}
                        </div>
                      );
                    },
                  )}
                  {props.exportConfig.filter.resolution.length > 0 && (
                    <p className="text-black font-semibold">
                      {props.exportConfig.filter.resolution.length}{" "}
                      {t("settings.integrations.exports.resolutions")}
                    </p>
                  )}
                  {props.exportConfig.filter.resolution.map(
                    (subscription, index) => {
                      return (
                        <div className="mb-1" key={`resolution-${index}`}>
                          {t(
                            ("newOrder.resolution" +
                              subscription) as Translation,
                          )}
                        </div>
                      );
                    },
                  )}
                </>
              }
            >
              <div>
                (
                {props.exportConfig.filter.status.length +
                  props.exportConfig.filter.resolution.length}{" "}
                {t("settings.integrations.exports.filters")} )
              </div>
            </Tooltip>
          </div>
        </div>

        <SimpleMenu
          iconTitle={<MoreHorizontal className="h-4 w-4" />}
          type={"neutral"}
          menu={[
            {
              onClick: () => setIsUpdating(true),
              title: t("settings.integrations.webhooks.menu.edit"),
              icon: <Pencil className="h-4 w-4 mr-1" />,
            },
            {
              onClick: () => setTasksIsOpen(!tasksIsOpen),
              title: tasksIsOpen
                ? t("settings.integrations.imports.hideTasks")
                : t("settings.integrations.imports.showTasks"),
              icon: <DatabaseIcon className="h-4 w-4 mr-1" />,
            },
            {
              onClick: () => setIsDeleting(true),
              renderTitle: () => (
                <p className="text-red-500">{t("misc.delete")}</p>
              ),
              icon: <TrashIcon className="h-4 w-4 mr-1 text-red-500" />,
            },
          ]}
        />
      </div>

      {tasksIsOpen && (
        <div className="space-y-2">
          <div className="flex space-x-2">
            <Select
              value={statusFilter || "all"}
              onValueChange={(value) =>
                setStatusFilter(value === "all" ? "" : value)
              }
            >
              <SelectTrigger className="h-8 w-40 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  {t("settings.integrations.imports.filterStatusAll")}
                </SelectItem>
                <SelectItem value="done">
                  {t("settings.integrations.imports.filterStatusDone")}
                </SelectItem>
                <SelectItem value="error">
                  {t("settings.integrations.imports.filterStatusError")}
                </SelectItem>
                <SelectItem value="running">
                  {t("settings.integrations.imports.filterStatusRunning")}
                </SelectItem>
                <SelectItem value="pending">
                  {t("settings.integrations.imports.filterStatusPending")}
                </SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={sinceDaysFilter ? String(sinceDaysFilter) : "all"}
              onValueChange={(value) =>
                setSinceDaysFilter(value === "all" ? undefined : Number(value))
              }
            >
              <SelectTrigger className="h-8 w-40 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">
                  {t("settings.integrations.imports.filterLast7Days")}
                </SelectItem>
                <SelectItem value="30">
                  {t("settings.integrations.imports.filterLast30Days")}
                </SelectItem>
                <SelectItem value="90">
                  {t("settings.integrations.imports.filterLast90Days")}
                </SelectItem>
                <SelectItem value="all">
                  {t("settings.integrations.imports.filterAllTime")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2 h-64 overflow-y-auto">
            {tasks?.map((task) => (
              <ExportTask key={task.id} task={task} />
            ))}
            {hasNextPage && (
              <button
                className="text-xs text-gray-400 underline"
                disabled={isFetchingNextPage}
                onClick={() => fetchNextPage()}
              >
                {isFetchingNextPage
                  ? t("misc.loading")
                  : t("settings.integrations.imports.loadMoreTasks")}
              </button>
            )}
          </div>
        </div>
      )}

      {isUpdating && (
        <UpdateExportSettingsDialog
          shopExport={props.exportConfig}
          open={isUpdating}
          onOpenChange={setIsUpdating}
        />
      )}

      {isDeleting && (
        <Dialog open={isDeleting} onOpenChange={setIsDeleting}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {t("settings.integrations.exports.removeExport.title")}
              </DialogTitle>
              <DialogDescription>
                {t("settings.integrations.exports.removeExport.description")}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex space-x-2 justify-end">
              <Button variant="outline" onClick={() => setIsDeleting(false)}>
                {t("misc.cancel")}
              </Button>
              <ButtonLoading
                isLoading={isDeletePending}
                variant="destructive"
                onClick={handleDelete}
              >
                {t("misc.delete")}
              </ButtonLoading>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

function ExportTask(props: { task: TaskListItemDto }) {
  const t = useTranslation();
  const [detailOpen, setDetailOpen] = useState(false);
  const { status, createdAt, result } = props.task;

  return (
    <div>
      <div className="flex text-sm space-x-4 text-gray-500 font-normal">
        <div>{format(createdAt, "yyyy-MM-dd HH:mm")}</div>
        <div className="flex-1">{result?.file?.name ?? "—"}</div>
        <ExportTaskStatus status={status} />
        {result?.error && (
          <button
            className="text-xs text-gray-400 underline"
            onClick={() => setDetailOpen((v) => !v)}
          >
            {t("misc.seemore")}
          </button>
        )}
      </div>
      {detailOpen && result?.error && (
        <pre className="mt-1 overflow-auto rounded bg-gray-50 p-2 text-xs text-gray-600">
          {result.error}
        </pre>
      )}
    </div>
  );
}

function ExportTaskStatus(props: { status: string }) {
  const t = useTranslation();
  if (props.status === "done") {
    return (
      <div className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
        <div className="-ml-0.5 mr-1.5 h-1.5 w-1.5 bg-green-400 rounded-full"></div>
        {t("misc.ok").toLowerCase()}
      </div>
    );
  }
  if (props.status === "error") {
    return (
      <div className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
        <div className="-ml-0.5 mr-1.5 h-1.5 w-1.5 bg-red-400 rounded-full"></div>
        {t("misc.error").toLowerCase()}
      </div>
    );
  }
  return (
    <div className="inline-flex items-center rounded-full bg-orange-100 px-2.5 py-0.5 text-xs font-medium text-orange-800">
      <div className="-ml-0.5 mr-1.5 h-1.5 w-1.5 bg-orange-400 rounded-full"></div>
      {props.status}
    </div>
  );
}
